// Post-reprocess result panel. Renders the *polled* status shape from
// GET /api/pipeline/reprocess-status/{run_id}:
//
//   { run_id, status, started_at_utc, completed_at_utc, elapsed_seconds,
//     summary: { ...runner result... } | null,
//     events: [{ action, performed_at, details_preview }] }
//
// For the brief window between POST and the first audit row being
// written, ``result`` is a synthetic {run_id, status: "running"} object
// so the panel can show a banner immediately.
//
// The runner's summary (when terminal) is ``result.summary`` and carries
// total_records / fully_mapped / with_gaps / excluded_ref_data /
// gap_summary.top_gap_types / email_status / error.

import { GAP_LABEL } from '../../constants/exclusions.js';

const STATUS_COLORS = {
  running: { border: '#FF6600', bg: '#fff3cd', fg: '#856404', label: 'Running' },
  unknown: { border: '#FF6600', bg: '#fff3cd', fg: '#856404', label: 'Starting…' },
  success: { border: '#28a745', bg: '#d4edda', fg: '#155724', label: 'Succeeded' },
  failed:  { border: '#dc3545', bg: '#f8d7da', fg: '#721c24', label: 'Failed' },
  skipped_upstream_not_ready: {
    border: '#f0ad4e', bg: '#fff3cd', fg: '#856404', label: 'Skipped — Upstream Not Ready',
  },
  no_data: { border: '#6c757d', bg: '#e9ecef', fg: '#495057', label: 'No Data' },
  no_changes: { border: '#28a745', bg: '#d4edda', fg: '#155724', label: 'Already Up to Date' },
  requires_full: {
    border: '#f0ad4e', bg: '#fff3cd', fg: '#856404', label: 'Full Reprocess Needed',
  },
};

// Ops-language explanations for status='requires_full' reasons (mirrors
// REQUIRES_FULL_MESSAGES in index.jsx so the panel is self-explanatory even
// after the banner is dismissed).
const REQUIRES_FULL_DETAIL = {
  rules_changed_since_last_full:
    'Billing rules changed since the last full reprocess. Rule changes affect every '
    + 'record, so a quick (partial) rebuild would leave the table inconsistent.',
  netsuite_synced_since_last_full:
    'NetSuite reference data was synced since the last full reprocess. Reference '
    + 'changes affect every record, so run a full Reprocess Billing.',
  no_successful_full_run:
    'No successful full reprocess exists yet.',
  no_watermarked_run:
    'The last full reprocess predates the quick-reprocess feature; one full run is '
    + 'needed to establish the change-tracking watermark.',
  adls_loader_running:
    'A data load is running right now. Try again in a few minutes.',
};

function numberFmt(n) {
  if (n === null || n === undefined) return '—';
  const v = Number(n);
  if (Number.isNaN(v)) return String(n);
  return v.toLocaleString();
}

function hoursFmt(n) {
  if (n === null || n === undefined) return '—';
  const v = Number(n);
  if (Number.isNaN(v)) return String(n);
  return v.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 2 });
}

// `scope` distinguishes a manual run (emailed only to whoever pressed the
// button) from a broadcast run. The scoped wording names `triggeredBy` rather
// than saying "you", because this panel also renders runs it did not start:
// the 409 handoff adopts whatever is already in flight, which may be the
// nightly timer's run or another user's.
function EmailLine({ status, force, scope, triggeredBy }) {
  if (!status) return null;
  let text;
  let color = '#495057';
  switch (status) {
    case 'sent':
      text = scope === 'requester'
        ? `Gap summary email sent only to ${triggeredBy || 'whoever started this run'} — manual runs do not email the billing group.`
        : 'Gap summary email sent to configured recipients.';
      color = '#b45309';
      break;
    case 'skipped_no_requester':
      text = 'No email sent — this run could not be attributed to a signed-in user.';
      break;
    case 'skipped_no_gaps':
      text = 'No gaps remain — no email sent.';
      break;
    case 'skipped_disabled':
      text = 'Email path disabled (ENABLE_BILLING_GAP_EMAILS=false).';
      break;
    case 'skipped_misconfigured':
      text = 'Email skipped — Microsoft Graph credentials not configured.';
      color = '#721c24';
      break;
    case 'skipped_no_recipients':
      text = 'Email skipped — BILLING_EMAIL_RECIPIENTS is empty.';
      break;
    case 'skipped_quick_mode':
      text = 'Quick run — no gap email sent (full reprocesses send the summary).';
      break;
    case 'failed':
      text = 'Email send failed (reprocess itself was unaffected). Check audit log.';
      color = '#721c24';
      break;
    default:
      text = `Email status: ${status}`;
  }
  return (
    <div style={{ fontSize: 13, color, marginTop: 8 }}>
      {text}
      {force ? ' (forced run — readiness gate bypassed)' : null}
    </div>
  );
}

function TopGapTypes({ items }) {
  if (!items || items.length === 0) return null;
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ fontSize: 13, color: '#555', marginBottom: 4 }}>Top gap types:</div>
      <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
        {items.map((g) => (
          <li key={g.gap_type}>
            <strong>{GAP_LABEL[g.gap_type] || g.gap_type}</strong>: {numberFmt(g.count)} records
            {g.billable_hours_at_stake != null
              ? <> · {hoursFmt(g.billable_hours_at_stake)} hours at stake</>
              : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

function RunningProgress({ events }) {
  if (!events || events.length === 0) {
    return (
      <div style={{ fontSize: 14 }}>
        Starting reprocess… (backend is kicking off the rebuild; this takes a few seconds.)
      </div>
    );
  }
  const last = events[events.length - 1];
  return (
    <div style={{ fontSize: 14 }}>
      Reprocess in progress. Latest step:
      <div style={{ marginTop: 4, fontSize: 13, color: '#555' }}>
        <code>{last.action}</code>
        {last.details_preview ? <> — {last.details_preview}</> : null}
      </div>
    </div>
  );
}

export function ReprocessResult({ result }) {
  if (!result) return null;

  const status = result.status || 'failed';
  const colors = STATUS_COLORS[status] || STATUS_COLORS.failed;
  const s = result.summary || {};

  return (
    <div
      style={{
        margin: '16px 0', padding: '16px 20px',
        background: '#f8f9fa', borderRadius: 8,
        border: '1px solid #dee2e6',
        borderLeft: `4px solid ${colors.border}`,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
        <strong style={{ color: '#002159' }}>Reprocess Billing</strong>
        <span
          style={{
            padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
            background: colors.bg, color: colors.fg,
          }}
        >
          {colors.label}
        </span>
      </div>

      {(status === 'running' || status === 'unknown') && (
        <>
          <RunningProgress events={result.events} />
          <div style={{ fontSize: 12, color: '#666', marginTop: 8, fontStyle: 'italic' }}>
            Polling every 10 seconds — safe to leave this page open.
          </div>
        </>
      )}

      {status === 'skipped_upstream_not_ready' && (
        <div style={{ fontSize: 14 }}>
          Reprocess was skipped — upstream data is not ready. No billing records were changed.
          {s.readiness?.reason ? (
            <div style={{ fontSize: 12, color: '#856404', marginTop: 6 }}>
              Reason: <code>{s.readiness.reason}</code>
            </div>
          ) : null}
        </div>
      )}

      {status === 'no_data' && (
        <div style={{ fontSize: 14 }}>
          No punches were found in <code>df.punches</code>. Reprocess did not run;
          billing records were not touched.
        </div>
      )}

      {status === 'failed' && (
        <div style={{ fontSize: 14, color: '#721c24' }}>
          Reprocess failed{s.error ? `: ${s.error}` : '.'}
        </div>
      )}

      {status === 'no_changes' && (
        <div style={{ fontSize: 14 }}>
          Nothing changed since the last reprocess — billing records are already
          up to date. No records were touched.
        </div>
      )}

      {status === 'requires_full' && (
        <div style={{ fontSize: 14 }}>
          Quick reprocess can&apos;t run right now — use the{' '}
          <strong>Reprocess Billing Records</strong> button instead.
          <div style={{ fontSize: 13, color: '#856404', marginTop: 6 }}>
            {REQUIRES_FULL_DETAIL[s.reason] || (s.reason ? <code>{s.reason}</code> : null)}
          </div>
        </div>
      )}

      {status === 'success' && (
        <div style={{ fontSize: 14 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 6 }}>
            <span>Total: <strong>{numberFmt(s.total_records)}</strong></span>
            <span>Ready / pending: <strong>{numberFmt(s.fully_mapped)}</strong></span>
            <span>
              Gaps: <strong style={{ color: (s.with_gaps || 0) > 0 ? '#b45309' : undefined }}>
                {numberFmt(s.with_gaps)}
              </strong>
            </span>
            <span>Excluded: <strong>{numberFmt(s.excluded_ref_data)}</strong></span>
            <span>Elapsed: {hoursFmt(result.elapsed_seconds ?? s.elapsed_seconds)} sec</span>
          </div>
          {s.run_mode === 'quick' && (
            <div style={{ fontSize: 13, color: '#555', marginBottom: 6 }}>
              Quick rebuild: <strong>{numberFmt(s.affected_employees)}</strong> employees
              {' · '}<strong>{numberFmt(s.affected_weeks)}</strong> weeks
              {' · '}<strong>{numberFmt(s.records_deleted)}</strong> old records replaced.
              Everything else was left untouched.
            </div>
          )}
          {s.escalated_from_quick && (
            <div style={{ fontSize: 13, color: '#856404', marginBottom: 6 }}>
              The change set was too large for a quick rebuild — a full reprocess ran
              instead (<code>{s.escalated_from_quick}</code>).
            </div>
          )}
          <TopGapTypes items={s.gap_summary?.top_gap_types} />
          <EmailLine
            status={s.email_status}
            force={s.force || result.force}
            scope={s.email_scope}
            triggeredBy={s.triggered_by}
          />
        </div>
      )}

      <div style={{ fontSize: 11, color: '#999', marginTop: 10 }}>
        run_id: <code>{result.run_id || '—'}</code>
        {(s.run_mode || result.run_mode) ? <> · {(s.run_mode || result.run_mode) === 'quick' ? 'quick' : 'full'} reprocess</> : null}
        {result.mode ? <> · via {result.mode}</> : null}
        {result.force ? <> · forced</> : null}
      </div>
    </div>
  );
}
